import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, User, Building2, Phone, Mail, Trash2, HardHat, Pencil } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const empty = {
  tipo: "azienda", ragione_sociale: "", nome: "", cognome: "",
  partita_iva: "", codice_fiscale: "", email: "", telefono: "",
  indirizzo: "", pec: "", sdi: "", note: "",
};

export function ClienteForm({ form, setForm }) {
  return (
    <div className="space-y-3">
      <Tabs value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
        <TabsList className="grid grid-cols-2">
          <TabsTrigger value="azienda" data-testid="cl-azienda"><Building2 className="h-3 w-3 mr-1" /> Azienda / P.IVA</TabsTrigger>
          <TabsTrigger value="persona" data-testid="cl-persona"><User className="h-3 w-3 mr-1" /> Persona fisica</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="grid grid-cols-2 gap-3">
        {form.tipo === "azienda" ? (
          <>
            <div className="col-span-2"><Label>Ragione sociale</Label><Input data-testid="cl-ragione" value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} /></div>
            <div><Label>Partita IVA</Label><Input data-testid="cl-piva" value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} /></div>
            <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} /></div>
            <div><Label>PEC</Label><Input value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
            <div><Label>Codice SDI</Label><Input value={form.sdi} onChange={(e) => setForm({ ...form, sdi: e.target.value })} /></div>
          </>
        ) : (
          <>
            <div><Label>Nome</Label><Input data-testid="cl-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div><Label>Cognome</Label><Input data-testid="cl-cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} /></div>
            <div className="col-span-2"><Label>Codice Fiscale</Label><Input data-testid="cl-cf" value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} /></div>
          </>
        )}
        <div><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
        <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="col-span-2"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} /></div>
        <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
      </div>
    </div>
  );
}

export const clienteEmpty = empty;

export default function Clienti() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const nav = useNavigate();

  const load = async () => { const { data } = await api.get("/clienti"); setList(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editingId) {
        await api.put(`/clienti/${editingId}`, form);
        toast.success("Cliente aggiornato");
      } else {
        await api.post("/clienti", form); toast.success("Cliente salvato");
      }
      setForm(empty); setEditingId(null); setOpen(false); load();
    } catch { toast.error("Errore"); }
  };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      tipo: c.tipo || "azienda",
      ragione_sociale: c.ragione_sociale || "",
      nome: c.nome || "",
      cognome: c.cognome || "",
      partita_iva: c.partita_iva || "",
      codice_fiscale: c.codice_fiscale || "",
      email: c.email || "",
      telefono: c.telefono || "",
      indirizzo: c.indirizzo || "",
      pec: c.pec || "",
      sdi: c.sdi || "",
      note: c.note || "",
    });
    setOpen(true);
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare cliente?")) return;
    try { await api.delete(`/clienti/${id}`); toast.success("Rimosso"); load(); } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Rubrica</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Clienti</h1>
          <p className="text-muted-foreground mt-1">Aggiungi aziende (P.IVA) o persone fisiche (codice fiscale). Li selezionerai nei cantieri.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-cliente" className="h-11"><Plus className="h-4 w-4 mr-2" /> Nuovo cliente</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editingId ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
            <ClienteForm form={form} setForm={setForm} />
            <Button data-testid="cl-save" onClick={save} className="h-11 mt-2">{editingId ? "Salva modifiche" : "Salva"}</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <Card key={c.id} className="card-elevated">
            <CardContent className="p-5 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {c.tipo === "azienda" ? <Building2 className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-primary" />}
                    <div className="font-bold">{c.label}</div>
                  </div>
                  <Badge variant="secondary" className="text-[10px] mt-1">{c.tipo}</Badge>
                </div>
                <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-cliente-${c.id}`}><Pencil className="h-3 w-3" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(c.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
              </div>
              {(c.partita_iva || c.codice_fiscale) && (
                <div className="text-xs text-muted-foreground">
                  {c.partita_iva && <>P.IVA {c.partita_iva}<br/></>}
                  {c.codice_fiscale && <>CF {c.codice_fiscale}</>}
                </div>
              )}
              {c.telefono && <a href={`tel:${c.telefono}`} className="flex items-center gap-1 text-xs hover:text-primary"><Phone className="h-3 w-3" />{c.telefono}</a>}
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-xs hover:text-primary"><Mail className="h-3 w-3" />{c.email}</a>}
              {Array.isArray(c.cantieri) && c.cantieri.length > 0 && (
                <div className="pt-2 mt-2 border-t border-border space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
                    <HardHat className="h-3 w-3" /> Cantieri ({c.cantieri.length})
                  </div>
                  {c.cantieri.map((ca) => (
                    <button
                      key={ca.id}
                      onClick={() => nav(`/cantieri/${ca.id}`)}
                      data-testid={`cliente-cantiere-${ca.id}`}
                      className="w-full text-left space-y-1 rounded px-2 py-1.5 hover:bg-secondary transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold truncate flex-1">{ca.nome}</div>
                        <span className={`text-xs font-bold tabular-nums shrink-0 ${ca.percentuale_pagamenti >= 100 ? "text-emerald-700" : ca.percentuale_pagamenti >= 50 ? "text-primary" : "text-amber-700"}`}>
                          {ca.percentuale_pagamenti.toFixed(0)}% pagato
                        </span>
                      </div>
                      <Progress value={ca.percentuale_pagamenti} className="h-1.5" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {list.length === 0 && <div className="text-muted-foreground col-span-full py-12 text-center">Nessun cliente. Aggiungi il primo.</div>}
      </div>
    </div>
  );
}
